/**
 * Header bar for the conversation detail drawer: channel icon, subject,
 * reference + customer meta, status pill, and the action toolbar (gated by
 * permissions + conversation status). All corners ≤3px.
 */
import { useState, type JSX, type ReactNode } from 'react'
import { ArrowLeftRight, Check, Copy, MailCheck, Reply, ShieldCheck, UserCheck, X } from 'lucide-react'
import { useAuthStore } from '../../../store/authStore.js'
import { getChannelConfig } from '../../../modules/conversations/channelConfig.js'
import { getOriginConfig } from './sourceConfig.js'
import type { ConversationDetailResponse } from '../../../modules/conversations/types.js'
import { formatCustomerName, getCustomerInitials, cleanIdentifier, isNumericOrRawId } from '../../../utils/formatCustomerName.js'
import { R, StatusPill } from './primitives.js'

type Conversation = ConversationDetailResponse['conversation']

export interface DetailHeaderProps {
  conversation: Conversation
  canSelfAssign: boolean
  /** Whether the conversation is assigned to the current agent. */
  isAssignedToMe: boolean
  /** Whether the current agent may reply — only the assignee. */
  canReply: boolean
  /** Whether the conversation has collaborators (blocks transfer, per KC2). */
  hasCollaborators: boolean
  /** All required additional properties are filled (transfer precondition). */
  requiredPropsFilled: boolean
  /** Whether the current user is the approver of a pending approval request. */
  canReview: boolean
  onReply: () => void
  onSelfAssign: () => void
  onTransfer: () => void
  onReview: () => void
  onSendForApproval: () => void
  onClosePanel: () => void
}

function ToolButton({
  label,
  icon,
  onClick,
  tone = 'ghost',
  badge,
  disabled = false,
  title,
}: {
  label: string
  icon: ReactNode
  onClick: () => void
  tone?: 'ghost' | 'primary' | 'warn'
  badge?: number
  disabled?: boolean
  title?: string | undefined
}): JSX.Element {
  // Enabled buttons use the logo blue; disabled stay greyed (visible, not hidden).
  const toneClass = disabled
    ? 'cursor-not-allowed text-slate-300'
    : tone === 'primary'
      ? 'bg-[#3366FF] text-white hover:bg-[#2952CC]'
      : 'text-[#3366FF] hover:bg-blue-50'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      className={`relative flex items-center gap-1.5 ${R} px-2.5 py-1.5 text-xs font-semibold transition-colors ${toneClass}`}
    >
      {icon}
      <span className="hidden lg:inline">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className={`ml-0.5 ${R} bg-amber-500 px-1.5 text-[10px] font-bold text-white`}>
          {badge}
        </span>
      )}
    </button>
  )
}

export default function DetailHeader(props: DetailHeaderProps): JSX.Element {
  const { conversation } = props
  const has = useAuthStore((s) => s.hasPermission)
  // Origin badge (portal source if present, else channel); keep the transport
  // channel too so both are visible.
  const origin = getOriginConfig(conversation.source, conversation.channelOrigin)
  const channel = getChannelConfig(conversation.channelOrigin)
  const showChannelToo = origin.label !== channel.label
  const isClosed = conversation.status === 'closed'
  const isFb = (conversation.channelOrigin ?? '').toLowerCase() === 'facebook'
  const isIg = (conversation.channelOrigin ?? '').toLowerCase() === 'instagram'
  const clientName = formatCustomerName(
    conversation.displayName,
    conversation.primaryHandle,
    conversation.channelOrigin,
    conversation.fbAccountHolderName,
  )
  const initials = getCustomerInitials(clientName)
  const cleanHandle = cleanIdentifier(conversation.primaryHandle)

  // Users with this permission may transfer even when required classification
  // properties are unset — everyone else must fill them first.
  const canTransferWithoutProperty = has('conversation:allow_transfer_without_property')
  const transferPropsBlocked = !props.requiredPropsFilled && !canTransferWithoutProperty

  const [copied, setCopied] = useState(false)
  const copyReferenceId = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(conversation.referenceId)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — silently no-op.
    }
  }

  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 py-3">
      {/* Identity */}
      <div className="flex min-w-0 items-center gap-3">
        <div className="relative shrink-0">
          <div className={`flex h-10 w-10 items-center justify-center ${R} bg-gradient-to-br from-slate-200 to-slate-300 text-sm font-bold text-slate-700`}>
            {initials}
          </div>
          <div className={`absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${origin.bg} ${origin.color}`}>
            <origin.icon className="h-3 w-3" />
          </div>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-base font-bold text-[#0f172a]">
              {conversation.subject || 'Conversation'}
            </h2>
            <StatusPill
              status={conversation.status}
              conversationStatusId={conversation.conversationStatusId}
              statusName={conversation.conversationStatusName}
              statusColor={conversation.conversationStatusColor}
            />
          </div>
          <div className="mt-0.5 flex items-center gap-2 truncate text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              Conversation ID : <span className="font-semibold text-[#3366FF]">{conversation.referenceId}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); void copyReferenceId() }}
                title={copied ? 'Copied!' : 'Copy Conversation ID'}
                aria-label="Copy Conversation ID"
                className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#3366FF]"
              >
                {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
              </button>
            </span>
            <span className="text-slate-300">|</span>
            <span className="truncate">
              Client : <span className="font-medium text-slate-700">{isIg && cleanHandle === clientName ? `@${clientName}` : clientName}</span>
              {cleanHandle && cleanHandle !== clientName && !isFb && !isNumericOrRawId(cleanHandle) && (
                <span className="ml-1 text-slate-400">({cleanHandle})</span>
              )}
            </span>
            <span className="text-slate-300">|</span>
            <span className="inline-flex items-center gap-1">
              <origin.icon className="h-3 w-3" />
              {origin.label}
              {showChannelToo && <span className="text-slate-400">· {channel.label}</span>}
            </span>
          </div>
        </div>
      </div>

      {/* Actions — each is HIDDEN when not applicable (rather than shown
          disabled) and appears active only when usable. Reply/Transfer require
          the conversation to be assigned to you first. */}
      <div className="flex shrink-0 items-center gap-1">
        {!isClosed && props.canReply && (
          <ToolButton
            label="Reply"
            tone="primary"
            icon={<Reply className="h-4 w-4" />}
            onClick={props.onReply}
          />
        )}
        {!isClosed && props.canReview && (
          <ToolButton
            label="Review"
            tone="ghost"
            icon={<ShieldCheck className="h-4 w-4" />}
            onClick={props.onReview}
          />
        )}
        {props.canSelfAssign && (
          <ToolButton
            label="Self Assign"
            icon={<UserCheck className="h-4 w-4" />}
            onClick={props.onSelfAssign}
          />
        )}
        {!isClosed && props.isAssignedToMe && !transferPropsBlocked && !props.hasCollaborators && (
          <ToolButton
            label="Transfer"
            icon={<ArrowLeftRight className="h-4 w-4" />}
            onClick={props.onTransfer}
          />
        )}
        {has('conversations:reply') && !isClosed && props.isAssignedToMe && props.requiredPropsFilled && (
          <ToolButton
            label="Send for Approval"
            icon={<MailCheck className="h-4 w-4" />}
            onClick={props.onSendForApproval}
          />
        )}
        <span className="mx-1 h-6 w-px bg-slate-200" />
        <button
          type="button"
          onClick={props.onClosePanel}
          title="Close panel"
          className={`flex h-8 w-8 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600`}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
